import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PaperPlaneRight, MagnifyingGlass, Eye, ArrowsClockwise, CheckCircle, XCircle, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SentEmailsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/email-logs', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Greska pri ucitavanju email loga');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const showDetail = async (id) => {
    try {
      const res = await api.get(`/email-logs/${id}`);
      setDetail(res.data);
    } catch {
      toast.error('Greska pri ucitavanju detalja');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('sr-Latn-ME', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const StatusBadge = ({ status, imap_status }) => {
    if (status === 'failed') {
      return <Badge className="bg-red-100 text-red-700 border-red-300"><XCircle size={12} className="mr-1" />Greška</Badge>;
    }
    if (imap_status === 'saved') {
      return <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle size={12} className="mr-1" />Poslan + Sačuvan</Badge>;
    }
    if (imap_status === 'failed') {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-300"><CheckCircle size={12} className="mr-1" />Poslan (nije u Sent)</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 border-blue-300"><CheckCircle size={12} className="mr-1" />Poslan</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><PaperPlaneRight size={26} />Poslati emailovi</h1>
          <p className="text-sm text-slate-500">Evidencija svih emailova poslatih iz CRM-a — dokaz da je klijent obaviješten.</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-btn"><ArrowsClockwise size={16} className="mr-2" />Osvjezi</Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Pretraga po emailu ili naslovu..." className="pl-10 bg-white border-slate-300" data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(load, 50); }}>
              <SelectTrigger className="w-48 bg-white border-slate-300" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi statusi</SelectItem>
                <SelectItem value="sent">Poslano</SelectItem>
                <SelectItem value="failed">Greška</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="filter-btn">Filtriraj</Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Datum</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Primalac</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Naslov</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Attach</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Akcije</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Ucitavanje...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Nema poslatih emailova</td></tr>
                ) : items.map(it => (
                  <tr key={it.id} className="hover:bg-slate-50" data-testid={`email-row-${it.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-mono text-xs">{fmtDate(it.sent_at)}</td>
                    <td className="px-3 py-2 text-slate-900 font-medium">{it.to_email}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-md truncate" title={it.subject}>{it.subject}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {it.has_attachment ? <span title={it.attachment_name}><FloppyDisk size={14} /></span> : '-'}
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} imap_status={it.imap_status} /></td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" onClick={() => showDetail(it.id)} data-testid={`view-${it.id}`}>
                        <Eye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500">Ukupno: <strong>{total}</strong> {loading ? '' : `(prikazano ${items.length})`}</div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalji email-a</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Datum:</strong> {fmtDate(detail.sent_at)}</div>
                <div><strong>Status:</strong> <StatusBadge status={detail.status} imap_status={detail.imap_status} /></div>
                <div><strong>Od:</strong> {detail.from_name} &lt;{detail.from_email}&gt;</div>
                <div><strong>Za:</strong> {detail.to_email}</div>
                <div className="col-span-2"><strong>Naslov:</strong> {detail.subject}</div>
                {detail.attachment_name && <div className="col-span-2"><strong>Attachment:</strong> {detail.attachment_name}</div>}
                {detail.imap_folder && <div className="col-span-2"><strong>Sačuvan u:</strong> {detail.imap_folder}</div>}
                {detail.imap_error && <div className="col-span-2 text-amber-700 text-xs"><strong>IMAP napomena:</strong> {detail.imap_error}</div>}
                {detail.error && <div className="col-span-2 text-red-700"><strong>Greška:</strong> {detail.error}</div>}
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[50vh] overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: detail.html_body || detail.plain_body || '' }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
